import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogContext";
import {
  ArrowLeft, Plus, ArrowRight, User, Kanban as KanbanIcon,
  Flag, Trash,
} from "@phosphor-icons/react";

const PRIORITY_STYLES = {
  low: "bg-slate-100 text-slate-700 border-slate-300",
  medium: "bg-blue-50 text-blue-700 border-blue-300",
  high: "bg-orange-50 text-orange-700 border-orange-300",
  urgent: "bg-red-50 text-red-700 border-red-400",
};

export default function Backlog() {
  const { id } = useParams();
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", task_type: "task", points: 0, assignee_id: "", due_date: "" });
  const [moveTarget, setMoveTarget] = useState("");
  const [filter, setFilter] = useState({ priority: "", task_type: "", assignee: "", q: "" });
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const canManage = user.role !== "employee";

  const load = async () => {
    try {
      const [p, b, sp] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/backlog`),
        api.get(`/projects/${id}/sprints`),
      ]);
      setProject(p.data);
      setTasks(b.data.tasks || []);
      setSprints((sp.data.sprints || []).filter(s => s.status === "planning" || s.status === "active"));
      // Employees also need the user list so the task drawer can render
      // assignee names + watchers. `/users` returns a safe minimal
      // projection for non-privileged callers.
      try {
        const u = await api.get("/users");
        setUsers(u.data);
      } catch { /* non-fatal */ }
    } catch (e) {
      console.warn("Backlog load failed:", e?.response?.status || e?.message);
    }
  };

  useEffect(() => { load(); }, [id]);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/tasks", {
        project_id: id,
        title: form.title,
        description: form.description,
        status: "todo",
        priority: form.priority,
        task_type: form.task_type,
        points: Number(form.points) || 0,
        assignee_id: form.assignee_id || null,
        due_date: form.due_date || null,
      });
      setShowCreate(false);
      setForm({ title: "", description: "", priority: "medium", task_type: "task", points: 0, assignee_id: "", due_date: "" });
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggle = (tid) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(tid)) n.delete(tid); else n.add(tid);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === visibleTasks.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleTasks.map(t => t.id)));
    }
  };

  const moveToSprint = async () => {
    if (!moveTarget || selected.size === 0) return;
    setError(""); setMsg("");
    try {
      const r = await api.post(`/sprints/${moveTarget}/tasks`, {
        task_ids: Array.from(selected),
      });
      setMsg(`Moved ${r.data.updated} task(s) to sprint.`);
      setSelected(new Set());
      setMoveTarget("");
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteTask = async (tid) => {
    const ok = await confirm({
      title: "Delete this task?",
      description: "This will permanently remove the task from the backlog.",
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/tasks/${tid}`);
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const usersById = Object.fromEntries(users.map(u => [u.id, u]));
  const visibleTasks = tasks.filter(t => {
    if (filter.priority && t.priority !== filter.priority) return false;
    if (filter.task_type && t.task_type !== filter.task_type) return false;
    if (filter.assignee && t.assignee_id !== filter.assignee) return false;
    if (filter.q) {
      const q = filter.q.toLowerCase();
      if (!(t.title || "").toLowerCase().includes(q) && !(t.description || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (!project) return <div className="p-12 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <Link to={`/projects/${id}`} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-[#002FA7]" data-testid="back-to-kanban">
          <ArrowLeft size={14} weight="bold" /> Back to board
        </Link>
        <div className="flex items-end justify-between mt-3 gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Backlog</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="backlog-title">
              {project.name} · Backlog
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Tasks not yet assigned to a sprint. Select multiple and drop them into a sprint to start planning.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/projects/${id}/sprints`} className="border border-border px-3 py-2 text-sm hover:bg-muted flex items-center gap-2" data-testid="link-sprints">
              <Flag size={14} weight="bold" /> Sprints
            </Link>
            {canManage && (
              <button onClick={() => setShowCreate(true)} className="btn-primary flex items-center gap-2" data-testid="btn-new-backlog-task">
                <Plus size={14} weight="bold" /> New task
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 lg:px-12 py-6 border-b border-border">
        <div className="flex flex-wrap items-center gap-3">
          <input
            placeholder="Search title/description…"
            value={filter.q}
            onChange={e => setFilter({...filter, q: e.target.value})}
            className="border border-border px-3 py-2 text-sm w-64"
            data-testid="backlog-search"
          />
          <select value={filter.priority} onChange={e => setFilter({...filter, priority: e.target.value})} className="border border-border px-3 py-2 text-sm bg-white">
            <option value="">All priorities</option>
            <option value="low">Low</option><option value="medium">Medium</option>
            <option value="high">High</option><option value="urgent">Urgent</option>
          </select>
          <select value={filter.task_type} onChange={e => setFilter({...filter, task_type: e.target.value})} className="border border-border px-3 py-2 text-sm bg-white">
            <option value="">All types</option>
            <option value="task">Task</option><option value="story">Story</option>
            <option value="bug">Bug</option><option value="epic">Epic</option>
          </select>
          {users.length > 0 && (
            <select value={filter.assignee} onChange={e => setFilter({...filter, assignee: e.target.value})} className="border border-border px-3 py-2 text-sm bg-white">
              <option value="">All assignees</option>
              {users.filter(u => project.members?.includes(u.id)).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )}
          <div className="text-xs text-muted-foreground font-mono-mini ml-auto">
            {visibleTasks.length} of {tasks.length} tasks
          </div>
        </div>

        {canManage && selected.size > 0 && (
          <div className="mt-4 flex items-center gap-3 flex-wrap border-t border-border pt-4">
            <div className="text-sm font-mono-mini uppercase">
              {selected.size} selected
            </div>
            <select value={moveTarget} onChange={e => setMoveTarget(e.target.value)} className="border border-border px-3 py-2 text-sm bg-white" data-testid="move-sprint-select">
              <option value="">Move to sprint…</option>
              {sprints.map(s => <option key={s.id} value={s.id}>{s.name} ({s.status})</option>)}
            </select>
            <button
              onClick={moveToSprint}
              disabled={!moveTarget}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="btn-move-to-sprint"
            >
              <ArrowRight size={14} weight="bold" /> Move
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Clear selection
            </button>
          </div>
        )}
      </div>

      <div className="p-8 lg:p-12 max-w-6xl">
        {error && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm mb-4">{error}</div>}
        {msg && <div className="border border-[#22C55E] bg-[#F0FDF4] text-[#166534] px-4 py-2 text-sm mb-4">{msg}</div>}

        {visibleTasks.length === 0 ? (
          <div className="border border-border p-8 text-center text-sm text-muted-foreground">
            {tasks.length === 0 ? "No backlog tasks yet." : "No tasks match your filters."}
          </div>
        ) : (
          <div className="border border-border">
            <div className="grid grid-cols-[36px_1fr_100px_100px_120px_140px_60px] px-4 py-3 border-b border-border bg-muted text-[10px] uppercase font-mono-mini text-muted-foreground">
              {canManage ? (
                <input type="checkbox" checked={selected.size === visibleTasks.length && visibleTasks.length > 0} onChange={toggleAll} data-testid="backlog-select-all" />
              ) : <span />}
              <div>Title</div>
              <div>Type</div>
              <div>Priority</div>
              <div>Points / Due</div>
              <div>Assignee</div>
              <div></div>
            </div>
            {visibleTasks.map(t => {
              const assignee = usersById[t.assignee_id];
              return (
                <div key={t.id} className="grid grid-cols-[36px_1fr_100px_100px_120px_140px_60px] px-4 py-3 border-b border-border last:border-b-0 hover:bg-muted/40 items-center" data-testid={`backlog-task-${t.id}`}>
                  {canManage ? (
                    <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} data-testid={`backlog-check-${t.id}`} />
                  ) : <span />}
                  <div className="min-w-0">
                    <Link to={`/projects/${id}?task=${t.id}`} className="text-sm font-medium hover:text-[#002FA7] block truncate">
                      {t.title}
                    </Link>
                    {t.description && <div className="text-[11px] text-muted-foreground truncate">{t.description}</div>}
                  </div>
                  <div className="text-[10px] uppercase font-mono-mini">{t.task_type || "task"}</div>
                  <div>
                    <span className={`text-[10px] uppercase font-mono-mini border px-1.5 py-0.5 ${PRIORITY_STYLES[t.priority] || ""}`}>{t.priority}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono-mini">
                    {t.points > 0 && <span>{t.points} pt</span>}
                    {t.points > 0 && t.due_date && <span> · </span>}
                    {t.due_date && <span>{t.due_date}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {assignee ? (
                      <span className="inline-flex items-center gap-1">
                        <User size={10} weight="bold" /> {assignee.name}
                      </span>
                    ) : <span className="italic">Unassigned</span>}
                  </div>
                  <div className="flex justify-end">
                    {canManage && (
                      <button onClick={() => deleteTask(t.id)} className="text-muted-foreground hover:text-[#FF3B30]" title="Delete task">
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-border w-full max-w-md p-8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="create-backlog-task-dialog">
            <div className="eyebrow mb-2">In backlog</div>
            <h2 className="font-display text-2xl font-bold mb-6">New backlog task</h2>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="eyebrow block mb-2">Title</label>
                <input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" data-testid="backlog-title-input" />
              </div>
              <div>
                <label className="eyebrow block mb-2">Description</label>
                <textarea rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" data-testid="backlog-desc-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Type</label>
                  <select value={form.task_type} onChange={e => setForm({...form, task_type: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white">
                    <option value="task">Task</option><option value="story">Story</option><option value="bug">Bug</option><option value="epic">Epic</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Points</label>
                  <input type="number" min="0" value={form.points} onChange={e => setForm({...form, points: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eyebrow block mb-2">Priority</label>
                  <select value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white">
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Due date</label>
                  <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="w-full border border-border px-3 py-2 text-sm" />
                </div>
              </div>
              {users.length > 0 && (
                <div>
                  <label className="eyebrow block mb-2">Assignee</label>
                  <select value={form.assignee_id} onChange={e => setForm({...form, assignee_id: e.target.value})} className="w-full border border-border px-3 py-2 text-sm bg-white">
                    <option value="">Unassigned</option>
                    {users.filter(u => project.members?.includes(u.id) || u.id === user.id).map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {error && <div className="text-sm text-[#FF3B30] border border-[#FF3B30] px-3 py-2">{error}</div>}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 border border-border px-4 py-2 text-sm hover:bg-muted">Cancel</button>
                <button type="submit" className="flex-1 btn-primary" data-testid="submit-backlog-task">Create task</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
